import { useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { setActionStatus, sendMessage } from "../chatBotSlice";
import {
  getActionExecutor,
  isBlockedActionType,
  validateTier0,
  STALE_ACTION_TIMEOUT_MS,
} from "./actionRegistry";
import type { ActionResult, ActionExecutionContext } from "./actionRegistry";
import { sanitizeActionParams } from "./sanitizeActionParams";
import usePermission from "../../hooks/usePermission";
import { Modules } from "../../../core/models/PermissionModels";
import type { ChatActionRequest } from "../models/chatBotModels";
import type { ActionResultPayload } from "../models/chatBotModels";

// ============================================================
// useActionExecutor — orchestrates the full action lifecycle:
// blocklist → registry → permission → sanitize → confirm → execute
// ============================================================

/** Map action module names to Modules constants for permission check */
const MODULE_MAP: Record<string, string> = {
  Machinery: Modules.Machinery,
  Servicing: Modules.Servicing,
  Inventory: Modules.Inventory,
};

/** Rate limit: max 5 writes per minute */
const RATE_LIMIT_MAX = 5;
const RATE_LIMIT_WINDOW_MS = 60_000;

interface UseActionExecutorResult {
  /** Execute an action after user confirmation, then report result to bot */
  executeAction: (actionRequest: ChatActionRequest, values: Record<string, any>, machineId?: string, threadId?: string) => Promise<ActionResult>;
  /** Cancel an action and report to bot */
  cancelAction: (actionRequest: ChatActionRequest, machineId?: string, threadId?: string) => void;
  /** Check if an action can be executed (permission + not blocked + not stale) */
  canExecute: (actionType: string, module: string) => {
    allowed: boolean;
    reason?: string;
  };
  /** Check if an action is stale (older than 15 min) */
  isStale: (createdAt: number) => boolean;
  /** Check if rate limit is exceeded */
  isRateLimited: () => boolean;
}

const useActionExecutor = (): UseActionExecutorResult => {
  const dispatch = useAppDispatch();
  const executedIds = useAppSelector((s) => s.chatBot.executedActionIds);

  // Rate limiting — in-memory counter (resets on navigation/unmount)
  const rateLimitRef = useRef<number[]>([]);

  // Permission checks for each module (hooks must be called unconditionally)
  const machineryPerm = usePermission(Modules.Machinery);
  const servicingPerm = usePermission(Modules.Servicing);
  const inventoryPerm = usePermission(Modules.Inventory);

  const getPermission = useCallback(
    (module: string) => {
      // Navigation module (nav.* actions) — no backend permission required, any authenticated user can switch context
      if (module === "Navigation") {
        return { canRead: true, canWrite: false, canAdmin: false, hasAccess: true, level: 1 };
      }
      const moduleKey = MODULE_MAP[module];
      if (moduleKey === Modules.Machinery) return machineryPerm;
      if (moduleKey === Modules.Servicing) return servicingPerm;
      if (moduleKey === Modules.Inventory) return inventoryPerm;
      return { canRead: false, canWrite: false, canAdmin: false, hasAccess: false, level: 0 };
    },
    [machineryPerm, servicingPerm, inventoryPerm]
  );

  const isStale = useCallback((createdAt: number): boolean => {
    return Date.now() - createdAt > STALE_ACTION_TIMEOUT_MS;
  }, []);

  const isRateLimited = useCallback((): boolean => {
    const now = Date.now();
    // Remove old entries outside the window
    rateLimitRef.current = rateLimitRef.current.filter(
      (t) => now - t < RATE_LIMIT_WINDOW_MS
    );
    return rateLimitRef.current.length >= RATE_LIMIT_MAX;
  }, []);

  const canExecute = useCallback(
    (actionType: string, module: string): { allowed: boolean; reason?: string } => {
      // SEC-REQ-4: blocklist check
      if (isBlockedActionType(actionType)) {
        return { allowed: false, reason: "blocked" };
      }

      // Registry check
      const executor = getActionExecutor(actionType);
      if (!executor) {
        return { allowed: false, reason: "unknown_action" };
      }

      // SEC-REQ-9: Tier 0 validation
      if (!validateTier0(executor)) {
        return { allowed: false, reason: "tier0_violation" };
      }

      // Permission check
      const perm = getPermission(module);
      if (executor.requiredPermission === "Write" && !perm.canWrite) {
        return { allowed: false, reason: "permission_denied" };
      }
      if (executor.requiredPermission === "Admin" && !perm.canAdmin) {
        return { allowed: false, reason: "permission_denied" };
      }
      if (executor.requiredPermission === "Read" && !perm.canRead) {
        return { allowed: false, reason: "permission_denied" };
      }

      // Rate limit check
      if (isRateLimited()) {
        return { allowed: false, reason: "rate_limited" };
      }

      return { allowed: true };
    },
    [getPermission, isRateLimited]
  );

  /**
   * Report action result back to bot for conversation continuity (MA-0b.3).
   * Best-effort: fires sendMessage thunk with action_result payload.
   * The bot continues the conversation (e.g., "SR created. Want to create a service request?").
   */
  const reportResultToBot = useCallback(
    (actionRequest: ChatActionRequest, result: ActionResult, machineId?: string, threadId?: string) => {
      const actionResultPayload: ActionResultPayload = {
        action_id: actionRequest.action_id,
        action_type: actionRequest.action_type,
        success: result.success,
        result: result.data,
      };
      if (!result.success) {
        actionResultPayload.reason = result.message;
      }
      // Fire sendMessage thunk — bot receives action_result and continues conversation
      try {
        dispatch(
          sendMessage({
            message: "",
            machineId,
            threadId,
            actionResult: actionResultPayload,
            originThreadId: threadId,
          })
        );
      } catch {
        // Best-effort — don't block the UI if result reporting fails
      }
    },
    [dispatch]
  );

  const executeAction = useCallback(
    async (
      actionRequest: ChatActionRequest,
      values: Record<string, any>,
      machineId?: string,
      threadId?: string
    ): Promise<ActionResult> => {
      const { action_id, action_type } = actionRequest;

      // Deduplicate: prevent double execution from SSE retry
      if (executedIds.includes(action_id)) {
        return {
          success: false,
          message: "Action already executed",
        };
      }

      // Get executor from registry
      const executor = getActionExecutor(action_type);
      if (!executor) {
        const errorResult: ActionResult = {
          success: false,
          message: `Unknown action type: ${action_type}`,
        };
        dispatch(
          setActionStatus({
            actionId: action_id,
            actionType: action_type,
            state: "error",
            error: errorResult.message,
            createdAt: Date.now(),
          })
        );
        return errorResult;
      }

      // Sanitize params (SEC-REQ-2)
      const { sanitized, errors } = sanitizeActionParams(values);
      if (errors.length > 0) {
        const errorResult: ActionResult = {
          success: false,
          message: `Validation errors: ${errors.join(", ")}`,
        };
        dispatch(
          setActionStatus({
            actionId: action_id,
            actionType: action_type,
            state: "error",
            error: errorResult.message,
            createdAt: Date.now(),
          })
        );
        return errorResult;
      }

      // Set executing state
      dispatch(
        setActionStatus({
          actionId: action_id,
          actionType: action_type,
          state: "executing",
          createdAt: Date.now(),
        })
      );

      // Record in rate limiter
      rateLimitRef.current.push(Date.now());

      try {
        // SEC-REQ-6: X-Action-Source header is injected via Axios interceptor
        // (the existing API classes use the global axios instance which handles auth)
        const execContext: ActionExecutionContext = { machineId, threadId };
        const result = await executor.execute(sanitized, execContext);

        // Set success state
        dispatch(
          setActionStatus({
            actionId: action_id,
            actionType: action_type,
            state: "success",
            result,
            createdAt: Date.now(),
          })
        );

        // MA-0b.3: Report success result back to bot for conversation continuity
        reportResultToBot(actionRequest, result, machineId, threadId);

        return result;
      } catch (err: unknown) {
        const errMsg = err instanceof Error ? err.message : "Action execution failed";
        const errorResult: ActionResult = {
          success: false,
          message: errMsg,
        };

        dispatch(
          setActionStatus({
            actionId: action_id,
            actionType: action_type,
            state: "error",
            error: errMsg,
            createdAt: Date.now(),
          })
        );

        // MA-0b.3: Report error result back to bot
        reportResultToBot(actionRequest, errorResult, machineId, threadId);

        return errorResult;
      }
    },
    [dispatch, executedIds, reportResultToBot]
  );

  const cancelAction = useCallback(
    (actionRequest: ChatActionRequest, machineId?: string, threadId?: string) => {
      // Post cancel result back to bot
      dispatch(
        setActionStatus({
          actionId: actionRequest.action_id,
          actionType: actionRequest.action_type,
          state: "error",
          error: "user_cancelled",
          createdAt: Date.now(),
        })
      );

      // MA-0b.3: Notify bot about cancellation using action_result field
      const cancelResult: ActionResult = {
        success: false,
        message: "user_cancelled",
      };
      reportResultToBot(actionRequest, cancelResult, machineId, threadId);
    },
    [dispatch, reportResultToBot]
  );

  return {
    executeAction,
    cancelAction,
    canExecute,
    isStale,
    isRateLimited,
  };
};

export default useActionExecutor;
